import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMyBookings } from '../hooks/useBookings.js';
import { useMe } from '../hooks/useAuth.js';
import { TRAILER_TYPE_MAP } from '../config/trailerTypes.js';
import type { BookingTransaction, TransactionState } from '@shared/api-types.js';

type Tab = 'both' | 'renter' | 'owner';

export function InboxPage() {
  const [tab, setTab] = useState<Tab>('both');
  const me = useMe();
  const { data, isLoading } = useMyBookings(tab);

  const userId = me.data?.user?.id;

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="text-3xl font-bold text-navy mb-1">Inbox</h1>
      <p className="text-navy-500 mb-6">Your booking requests, conversations, and trips.</p>

      <div className="flex gap-1 border-b border-navy-50 mb-4 overflow-x-auto">
        <Tab label="All" active={tab === 'both'} onClick={() => setTab('both')} />
        <Tab label="Renting" active={tab === 'renter'} onClick={() => setTab('renter')} />
        <Tab label="Listing" active={tab === 'owner'} onClick={() => setTab('owner')} />
      </div>

      {isLoading && <p className="text-navy-500">Loading…</p>}
      {data?.transactions.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-navy-600 mb-4">No bookings yet.</p>
          <Link to="/search" className="btn-primary">
            Find a trailer
          </Link>
        </div>
      )}

      <div className="space-y-3">
        {data?.transactions.map((t) => (
          <BookingRow key={t.id} t={t} youAreOwner={t.ownerId === userId} />
        ))}
      </div>
    </div>
  );
}

function BookingRow({ t, youAreOwner }: { t: BookingTransaction; youAreOwner: boolean }) {
  const cover = t.listing?.photos[0];
  return (
    <Link
      to={`/transactions/${t.id}`}
      className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
    >
      <div className="w-20 h-20 rounded-lg bg-navy-50 overflow-hidden flex-shrink-0">
        {cover ? (
          <img
            src={cover.thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl">
            {t.listing ? TRAILER_TYPE_MAP[t.listing.trailerType]?.emoji ?? '🛻' : '🛻'}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-navy-500">
          <span className="font-mono">{t.code}</span>
          <span>·</span>
          <span>{youAreOwner ? 'You are the owner' : 'You are renting'}</span>
        </div>
        <div className="font-semibold text-navy truncate">{t.listing?.title ?? 'Booking'}</div>
        <div className="text-sm text-navy-500 truncate">
          {new Date(t.startsAt).toLocaleDateString('en-CA', {
            month: 'short',
            day: 'numeric',
          })}{' '}
          –{' '}
          {new Date(t.endsAt).toLocaleDateString('en-CA', {
            month: 'short',
            day: 'numeric',
          })}{' '}
          · ${(t.totalCents / 100).toFixed(0)}
        </div>
      </div>
      <StateBadge state={t.state} />
    </Link>
  );
}

function StateBadge({ state }: { state: TransactionState }) {
  const map: Record<TransactionState, { label: string; cls: string }> = {
    inquiry: { label: 'Inquiry', cls: 'bg-orange-100 text-orange-700' },
    pending_payment: { label: 'Awaiting payment', cls: 'bg-orange-100 text-orange-700' },
    confirmed: { label: 'Confirmed', cls: 'bg-green-100 text-green-700' },
    in_progress: { label: 'In progress', cls: 'bg-orange-100 text-orange-800' },
    returned: { label: 'Returned', cls: 'bg-navy-100 text-navy-700' },
    completed: { label: 'Completed', cls: 'bg-green-100 text-green-700' },
    reviewed: { label: 'Reviewed', cls: 'bg-green-100 text-green-700' },
    cancelled: { label: 'Cancelled', cls: 'bg-navy-100 text-navy-500' },
    disputed: { label: 'Disputed', cls: 'bg-red-100 text-red-700' },
    resolved: { label: 'Resolved', cls: 'bg-navy-100 text-navy-700' },
    rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  };
  const s = map[state] ?? { label: state, cls: 'bg-navy-100 text-navy-700' };
  return (
    <span
      className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

function Tab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-orange text-orange'
          : 'border-transparent text-navy-500 hover:text-navy'
      }`}
    >
      {label}
    </button>
  );
}
